<template>
  <sign-in-dialog
    v-if="!isSignedIn"
    :value="true"
    @input="$nextTick(() => isSignedIn || $emit('input', false))"
  >
    Please sign in to add a subscription
  </sign-in-dialog>

  <org-sign-up-dialog
    v-else-if="code == 'MULTI-USER' && !global.profile.org"
    :value="true"
    @cancel="$emit('input', false)"
  >
    <v-card-subtitle class="warning--text font-weight-bold">You must be an organization administrator to sign up for a multi-user subscription</v-card-subtitle>
  </org-sign-up-dialog>

  <confirm-dialog
    v-else-if="code == 'MULTI-USER' && !global.profile.org_admin"
    msg="You must be an organization administrator to manage your organization's subscriptions. Please contact us with any questions: <a href='mailto:contact@ellacard.com'>contact@ellacard.com</a>"
    primary="Ok"
    :buttons="[ 'Ok' ]"
    :persistent="false"
    :resolve="btn => $emit('input', false)"
  />

  <confirm-dialog
    v-else-if="code == 'SINGLE-USER' && global.profile.org"
    msg="You are a member of an Ellacard organization and cannot enroll in the single-user subscription plan. Please contact us with any questions: <a href='mailto:contact@ellacard.com'>contact@ellacard.com</a>"
    primary="Ok"
    :buttons="[ 'Ok' ]"
    :persistent="false"
    :resolve="btn => $emit('input', false)"
  />

  <v-dialog-loader v-else-if="loadAccountStatus.pending" width="500" :value="value" v-bind="$attrs" v-on="$listeners" />

  <v-dialog
    v-else-if="activeSubscription"
    width="500"
    :value="value"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <v-card>
      <v-btn class="float-right mt-3 mr-3" icon @click="$emit('input', false)"><v-icon v-html="mdiClose" /></v-btn>
      <v-card-title>Unlimited subscription</v-card-title>
      <v-card-subtitle class="overline short">{{code}}</v-card-subtitle>
      <v-card-subtitle class="warning--text font-weight-bold">
        Your current subscription has not yet expired. Please go to <router-link :to="{ name: 'account' }">your account</router-link> to manage your current subscription.
      </v-card-subtitle>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn @click="$emit('input', false)">Cancel</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog-loader
    v-else
    width="500"
    :loading="!didLoadPayment"
    :value="value"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <v-card>
      <v-btn class="float-right mt-3 mr-3" icon @click="$emit('input', false)"><v-icon v-html="mdiClose" /></v-btn>
      <v-card-title>Unlimited subscription</v-card-title>
      <v-card-subtitle class="overline short">{{code}}</v-card-subtitle>
      <v-card-subtitle v-if="code == 'MULTI-USER'" class="warning--text font-weight-bold">
        Subscription will be applied to your organization
      </v-card-subtitle>
      <v-divider />
      <v-progress-linear v-if="paymentIsLoading" class="absolute" style="width: 100%;" indeterminate />
      <v-card-text v-if="code == 'MULTI-USER' && !global.profile?.org" class="pt-6 error lighten-5">
        <!-- TODO: just show the dialog here -->
        A multi-user subscription requires a business account
      </v-card-text>
      <v-card-text v-else-if="code == 'MULTI-USER' && global.profile?.org && !global.profile?.org_admin" class="pt-6">
        Only organization administrators can add a business subscription
      </v-card-text>
      <v-card-text v-else-if="code == 'MULTI-USER'" class="pt-5">
        <v-row>
          <v-col cols="12" sm="6">
            <div class="text--label-active mb-1">Subscription cadence</div>
            <div class="btn-group">
              <v-btn class="btn-group-right" :input-value="cadence == 'MONTHLY'" @click="cadence = 'MONTHLY'">Monthly</v-btn>
              <v-btn class="btn-group-left" :input-value="cadence == 'ANNUAL'" @click="cadence = 'ANNUAL'">Annual</v-btn>
            </div>
          </v-col>
          <v-col v-if="code == 'MULTI-USER'" cols="12" sm="6">
            <div class="text--label-active mb-1">Number of shared users</div>
            <v-select
              style="height: 36px;"
              dense solo hide-details
              :items="TIERS.map(x => ({ text: x, value: x, disabled: parseInt(x.split(' – ')[1]) < nExistingUsers }))"
              v-model="adjustedTier"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text v-else-if="showSpecialOffer" class="pt-5">

        <v-dialog v-if="resolveConfirmPromo" width="300" :value="true" @input="resolveConfirmPromo">
          <v-card>
            <v-card-title>
              Gift card credit applied
            </v-card-title>
            <v-divider />
            <v-card-text class="pt-5">
              Add any gift card to your card using the gift card button in the "Sign / design" view.
              <div class="my-2 text-center">
                <v-btn dark fab x-small color="green lighten-2"><v-icon v-html="mdiWalletGiftcard" /></v-btn>
              </div>
              Credit will be applied automatically on checkout.
            </v-card-text>
            <v-divider />
            <v-card-actions class="justify-end">
              <v-btn text color="primary" @click="resolveConfirmPromo">Ok</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <div class="text--label-active red--text">Special offer</div>
        <div>
          $10 gift card with purchase of single-user subscription. Credit applied automatically on purchase.
        </div>
      </v-card-text>

      <payment
        class="pa-6"
        :metadata="paymentMetadata"
        :loading.sync="paymentIsLoading"
        @complete="onComplete"
        @loaded="didLoadPayment = true"
      />
      <v-divider />
      <v-card-text class="pa-4 caption font-italic">
        <div class="mb-4">Prefer to pay by invoice? Please contact us: <a :href="mailto">contact@ellacard.com</a></div>
        You will be billed automatically on {{paymentMetadata.cadence == 'ANNUAL' ? 'an annual' : 'a monthly'}} basis. You can manage your subscription preferences and cancel at any time from your account.
      </v-card-text>
    </v-card>
  </v-dialog-loader>
</template>


<script>
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import OrgSignUpDialog from '@/components/OrgSignUpDialog.vue';
import SignInDialog from '@/components/SignInDialog.vue';
import VDialogLoader from '@/components/VDialogLoader.vue';
import { mdiClose, mdiWalletGiftcard } from '@mdi/js';


// Note that this does not include the '> 250' tier
const TIERS = [ ...[ ...Array(10).keys() ].map(i => `${i * 25 + 1} – ${(i + 1) * 25}`) ];


export default {
  name: 'buy-subscription-dialog',

  components: {
    ConfirmDialog,
    OrgSignUpDialog,
    SignInDialog,
    VDialogLoader,

    // I'm only loading this asynchronously because the component wasn't being found by Vue in come cases otherwise,
    // perhaps because of the if-else pattern I use on the root element?
    Payment: () => import('@/components/Payment.vue')
  },

  props: [ 'value', 'code', 'tier', 'initialCadence' ],

  data() {
    return {
      resolveConfirmPromo: null,
      didLoadPayment: false,
      cadence: this.initialCadence || 'MONTHLY',
      adjustedTier: '1 – 25',
      account: null,
      nExistingUsers: 0,
      paymentIsLoading: false,
      loadAccountStatus: this.$track('loadAccount')
    };
  },

  computed: {
    paymentMetadata() {
      const code = this.code;
      const cadence = (code == 'SINGLE-USER') ? 'ANNUAL' : this.cadence;
      const max = (TIERS.indexOf(this.adjustedTier) + 1) * 25;
      const m = { target: 'SUBSCRIPTION', code, cadence, max };
      if (this.global.isEligibleForPromo && (code == 'SINGLE-USER'))
        m.promo = 'GIFTCARD_CREDIT';
      return m;
    },

    mailto() {
      const code = this.code;
      const subject = 'Subscription invoice requested';
      const cadence = (code == 'SINGLE-USER') ? 'ANNUAL' : this.cadence;
      let body = `Selected plan: ${code} \nCadence: ${cadence}`;
      if (code == 'MULTI-USER')
        body += ` \nTier: ${this.adjustedTier} \nOrganization ID: ${this.global.profile?.org_id}`;
      return `mailto:contact@ellacard.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    },

    activeSubscription() {
      return this.account?.subscription && !this.account.subscription.payment_failed_msg ? this.account.subscription : null;
    },

    showSpecialOffer() {
      return this.global.isEligibleForPromo && (this.code == 'SINGLE-USER');
    }
  },

  created() {
    Object.assign(this, { mdiClose, mdiWalletGiftcard, TIERS });
  },

  methods: {
    async loadAccount() {
      this.account = null;
      if (this.isSignedIn) {
        this.account = await this.$call('get-account');
        if (this.account.org)
          this.nExistingUsers = (await this.$call('list-org-user-emails')).length;
      }
    },

    async onComplete() {
      if (this.global.isEligibleForPromo) {
        await new Promise(r => this.resolveConfirmPromo = r);
        this.resolveConfirmPromo = null;
      }
      this.$emit('purchase');
      this.$emit('input', false);
      this.global.isEligibleForPromo = false;
    }
  },

  watch: {
    tier: {
      immediate: true,
      handler(v) {
        this.adjustedTier = v;
      }
    },

    nExistingUsers(v) {
      this.adjustedTier = TIERS[ Math.floor((v - 1) / 25) ];
    },

    globalUserId: {
      immediate: true,
      handler() {
        this.loadAccount();
      }
    }
  }
}
</script>
